import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useTheme } from "@mui/material/styles";
import { QuickSale_PAP_ACTIVITY_Update } from "../../../Server/ApiV2";
import { fAlertToast } from "../../../Utility/Utilitys";
import moment from "moment";
const ActivityModal = ({ open, onClose, transId, userCd, loadSummary, fetchActivities, activities }) => {
  const theme = useTheme();
  const [comment, setComment] = useState("");
  const listBottomRef = useRef(null);
  const handleAdd = async () => {
    if (!comment.trim()) return;
    try {
      const response = await QuickSale_PAP_ACTIVITY_Update({
        TransId: transId || "",
        SrNo: 0,
        Narration: comment.trim(),
        UserCd: userCd || ""
      });
      if (response?.status === 200 && response?.data?.Type === "Success") {
        setComment("");
        await fetchActivities();
        await loadSummary()
      }
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.error || err?.message);
    }
  };

  useEffect(() => {
    if (open && listBottomRef.current) {
      listBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activities, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          padding: "8px !important",
          pr: 6,
          backgroundColor: theme.palette["custom-grid-warning"].main,
          color: "#fff",
        }}
      >
        Recent Activity
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 5, top: 5, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 0, height: "60vh", overflowY: "auto", display: "flex", flexDirection: "column", justifyContent: "space-between" }}
      >
        <Box sx={{ maxHeight: "80vh", overflowY: "auto", px: 2 }}>
          <List disablePadding>
            {activities?.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 2, py: 3, textAlign: "center" }}
              >
                No recent activity
              </Typography>
            )}
            {activities?.map((it, idx) => (
              <React.Fragment key={it.id ?? idx}>
                <ListItem
                  disableGutters
                  sx={{
                    px: 1,
                    py: 1,
                    alignItems: "center",
                    borderRadius: 1.5,
                    transition:
                      "background-color .2s ease, box-shadow .2s ease",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: (t) => t.palette.info.main,
                        color: "common.white",
                        width: 36,
                        height: 36,
                        mt: 0.5,
                      }}
                    >
                      <ChatBubbleOutlineIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "pre-wrap", mt: 0.3 }}
                    >
                      {it.Narration}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ mt: 0.8 }}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<PersonOutlineIcon color="primary" sx={{ fontSize: 15 }} />}
                        label={it.EnBy || "—"}
                        sx={{
                          height: 24,
                          fontSize: "0.75rem",
                          "& .MuiChip-label": { px: 0.8 },
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                        }}
                      />
                      <Tooltip title={it.EnDt || ""}>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<AccessTimeIcon color="primary" sx={{ fontSize: 15 }} />}
                          label={moment
                            .utc(it.EnDt)
                            .format("DD/MM/YYYY hh:mm A")}
                          sx={{
                            height: 24,
                            fontSize: "0.75rem",
                            "& .MuiChip-label": { px: 0.8 },
                            borderColor: theme.palette.primary.main, color: theme.palette.primary.main
                          }}
                        />
                      </Tooltip>
                    </Stack>
                  </Box>
                </ListItem>
                <Divider sx={{ mx: 1 }} />
              </React.Fragment>
            ))}
          </List>
        </Box>
        <Box sx={{ p: 1 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
          >
            <TextField
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment…"
              fullWidth
              size="small"
              minRows={2}
              multiline
              sx={{ maxHeight: "20vh", overflowY: "auto" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Send">
                      <span>
                        <IconButton size="small" color="primary" onClick={handleAdd}>
                          <SendRoundedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityModal;